import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { billingApi, euro } from '@/services/billingApi';
import { toast } from 'sonner';

export function BillingClientPanel({ clientId }: { clientId: string }) {
  const [data, setData] = useState<any>(null);
  const [pay, setPay] = useState({ amount: '', method: 'BONIFICO', paidAt: new Date().toISOString().slice(0, 10), notes: '' });
  const [open, setOpen] = useState({ amount: '', sign: 'DEBIT', entryDate: new Date().toISOString().slice(0, 10), notes: '' });

  const load = () => {
    billingApi.statement(clientId).then(setData).catch((e) => toast.error(e.response?.data?.message || 'Errore conto'));
  };
  useEffect(() => { load(); }, [clientId]);

  if (!data) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Conto</CardTitle>
        <div className="flex gap-2">
          <Link to={`/preventivi-e-conti/nuovo?type=QUOTE&clientId=${clientId}`}>
            <Button size="sm" variant="outline">Preventivo</Button>
          </Link>
          <Link to={`/preventivi-e-conti/nuovo?type=INVOICE&clientId=${clientId}`}>
            <Button size="sm">Fattura</Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-2xl font-bold">{euro(data.balance)} <span className="text-sm font-normal text-gray-500">saldo (positivo = da incassare)</span></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Data</th>
                <th>Descrizione</th>
                <th className="text-right">Dare</th>
                <th className="text-right">Avere</th>
                <th className="text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.movements.map((m: any) => (
                <tr key={`${m.kind}-${m.id}`} className="border-b">
                  <td className="py-2">{new Date(m.date).toLocaleDateString('it-IT')}</td>
                  <td>
                    {['INVOICE', 'CREDIT_NOTE', 'QUOTE'].includes(m.kind) ? (
                      <Link className="text-blue-600" to={`/preventivi-e-conti/${m.id}`}>{m.description}</Link>
                    ) : m.description}
                  </td>
                  <td className="text-right">{m.debit ? euro(m.debit) : ''}</td>
                  <td className="text-right">{m.credit ? euro(m.credit) : ''}</td>
                  <td className="text-right font-medium">{euro(m.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 border rounded-md p-3">
            <div className="font-medium text-sm">Registra pagamento</div>
            <Input type="date" value={pay.paidAt} onChange={(e) => setPay({ ...pay, paidAt: e.target.value })} />
            <Input type="number" step="0.01" placeholder="Importo" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
            <select className="w-full border rounded-md h-10 px-2" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
              <option>BONIFICO</option><option>CONTANTI</option><option>POS</option><option>ASSEGNO</option><option>ALTRO</option>
            </select>
            <Input placeholder="Note" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} />
            <Button size="sm" onClick={async () => {
              if (!pay.amount || Number(pay.amount) <= 0) {
                toast.error('Inserisci un importo');
                return;
              }
              try {
                const saved: any = await billingApi.createPayment({ clientId, paidAt: pay.paidAt, amount: Number(pay.amount), method: pay.method, notes: pay.notes });
                setPay({ ...pay, amount: '', notes: '' });
                load();
                const allocated = Number(saved.allocated || 0);
                const remainder = Number(saved.remainder || 0);
                if (allocated > 0 && remainder <= 0.001) toast.success('Pagamento registrato e applicato alle fatture');
                else if (allocated > 0) toast.success('Pagamento registrato e applicato in parte alle fatture');
                else toast.success('Pagamento registrato (nessuna fattura aperta)');
              } catch (e: any) {
                toast.error(e.response?.data?.message || 'Errore pagamento');
              }
            }}>Salva pagamento</Button>
          </div>
          <div className="space-y-2 border rounded-md p-3">
            <div className="font-medium text-sm">Saldo di apertura</div>
            <Input type="date" value={open.entryDate} onChange={(e) => setOpen({ ...open, entryDate: e.target.value })} />
            <select className="w-full border rounded-md h-10 px-2" value={open.sign} onChange={(e) => setOpen({ ...open, sign: e.target.value })}>
              <option value="DEBIT">Dare (ci deve)</option>
              <option value="CREDIT">Avere (abbiamo un credito suo)</option>
            </select>
            <Input type="number" step="0.01" placeholder="Importo" value={open.amount} onChange={(e) => setOpen({ ...open, amount: e.target.value })} />
            <Input placeholder="Note" value={open.notes} onChange={(e) => setOpen({ ...open, notes: e.target.value })} />
            <Button size="sm" variant="outline" onClick={async () => {
              await billingApi.saveOpening({ clientId, entryDate: open.entryDate, sign: open.sign, amount: Number(open.amount), notes: open.notes });
              setOpen({ ...open, amount: '', notes: '' });
              load();
              toast.success('Apertura salvata');
            }}>Salva apertura</Button>
          </div>
        </div>
        {data.payments?.filter((p: any) => Number(p.remainder) > 0.001).length > 0 && (
          <div className="text-sm">
            <div className="font-medium mb-1">Pagamenti (residuo non allocato)</div>
            {data.payments.filter((p: any) => Number(p.remainder) > 0.001).map((p: any) => (
              <div key={p.id} className="flex justify-between border-b py-1">
                <span>{new Date(p.paidAt).toLocaleDateString('it-IT')} {p.method} {euro(p.amount)} — residuo {euro(p.remainder)}</span>
                <Button size="sm" variant="ghost" onClick={async () => { await billingApi.deletePayment(p.id); load(); }}>Elimina</Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
